import React from 'react';
import axios from 'axios';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { HookEndpointResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import usePostFnolHookData from './usePostFnolHookData';

const validationSchema = Yup.object().shape({
  is_active: Yup.boolean(),
  hook_endpoint_key: Yup.string().when('is_active', {
    is: true,
    then: (schema) => schema.required('Required'),
  }),
});

interface PostFnolHookDialogProps {
  onClose: () => void;
  organizationId: number;
  incidentConfigurationId: number;
}

interface FormValues {
  is_active: boolean;
  hook_endpoint_key: string;
}

const PostFnolHookDialogInner: React.FC<{
  onClose: () => void;
  endpoints: HookEndpointResponse[];
}> = ({ onClose, endpoints }) => {
  const { handleSubmit, isSubmitting, values } = useFormikContext<FormValues>();

  return (
    <CardDialog
      title="Post-FNOL Hook"
      isDialog
      open={true}
      maxWidth="sm"
      onClose={onClose}
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSubmit}
          onClickSecondary={onClose}
          primaryLabel="Save"
          showPrimary
          secondaryLabel="Cancel"
          disabled={isSubmitting}
        />
      }
    >
      <div className="flex flex-col gap-20">
        <SwitchFormik id="is_active" label="Active" />
        <TextFieldFormik id="hook_endpoint_key" label="Hook Endpoint" select fullWidth disabled={!values.is_active}>
          {/*eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
          {/*@ts-ignore*/}
          {endpoints.map((ep) => (
            <MenuItem key={ep.key} value={ep.key}>
              {ep.name}
            </MenuItem>
          ))}
        </TextFieldFormik>
      </div>
    </CardDialog>
  );
};

const PostFnolHookDialog: React.FC<PostFnolHookDialogProps> = ({
  onClose,
  organizationId,
  incidentConfigurationId,
}) => {
  const { binding, endpoints, isLoading, bindingUrl } = usePostFnolHookData(organizationId, incidentConfigurationId);

  const handleSubmit = async (values: FormValues) => {
    try {
      if (values.is_active) {
        await axios.put(bindingUrl, {
          hook_endpoint_key: values.hook_endpoint_key,
          is_active: true,
        });
      } else if (binding) {
        await axios.delete(bindingUrl);
      }
      onClose();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  if (isLoading) {
    return null;
  }

  const initialValues: FormValues = {
    is_active: binding?.is_active ?? false,
    hook_endpoint_key: binding?.hook_endpoint?.key ?? '',
  };

  return (
    <Formik<FormValues> initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      <PostFnolHookDialogInner onClose={onClose} endpoints={endpoints} />
    </Formik>
  );
};

export default PostFnolHookDialog;
